import { i18n_t as $t } from '@/utils/i18n';

// Object-label override helper.
//
// During the experimental phase, customers piloting Object
// Analytics tend to get tripped up on the word "objects." A
// project-tracking customer thinks of them as projects; a service-
// ticket customer thinks of them as tickets; an account-team
// customer thinks of them as accounts. Hardcoding "Objects" /
// "Object analytics" all over the UI buries the actual lifecycle
// they care about under generic terminology.
//
// This module is a small, local override layer: per-object-
// property labels with three slots — `pageTitle`, `singular`,
// `plural` — that the user can edit via a modal. Overrides are
// keyed by the object property's `field`, so picking a different
// property swaps the labels (or falls back to the defaults if no
// override exists for that property yet).
//
// The override stays local-and-experimental for now. Long-term,
// this lives as a subscription-level setting attached to the
// object-property registration. Treat that as a future migration:
// the persisted shape here (Record<field, ObjectLabels>) maps
// cleanly to a subscription-level "label per property" config.
//
// Locale handling: this helper provides English-style capitalized
// variants (first character uppercased). For locales where
// sentence-start capitalization doesn't apply (Japanese, etc.),
// the i18n layer's string templates remain the source of truth
// and the {Singular} / {Plural} placeholders accept whatever the
// translator provides.

export type ObjectLabels = {
    pageTitle: string;
    singular: string;
    plural: string;
};

export type ObjectLabelOverridesMap = Record<string, ObjectLabels>;

export { OBJECT_ANALYTICS_LABEL_OVERRIDES_SETTING_NAME } from './object-label.constants';

export const DEFAULT_LABELS: ObjectLabels = Object.freeze({
    pageTitle: 'Object analytics',
    singular: 'object',
    plural: 'objects',
});

/**
 * Capitalize the first character of `value` and lowercase the
 * rest is NOT done — we only upper the first character to
 * preserve any user-entered casing the rest of the word might
 * carry (e.g., "iOS account" → "IOS account" would be wrong; we
 * keep "iOS account").
 */
export function capitalize (value: string): string {
    if (typeof value !== 'string' || value.length === 0) return value;

    return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Resolve the active labels for a given object property field.
 * Falls back to `defaults` (English `DEFAULT_LABELS` unless the
 * caller supplies locale-resolved defaults — see the OA page, which
 * passes translated singular/plural so un-customized labels stay in
 * the user's language) when:
 *   - `field` is not set (no object property picked yet),
 *   - the overrides map doesn't contain an entry for `field`,
 *   - the override entry is missing required slots (defensive).
 *
 * Returns a fresh object so callers can safely spread / mutate
 * without affecting the source overrides map.
 */
export function getEffectiveLabels (
    field: string | null | undefined,
    overrides: ObjectLabelOverridesMap | null | undefined,
    defaults: ObjectLabels = DEFAULT_LABELS,
): ObjectLabels {
    if (!field || !overrides) {
        return { ...defaults };
    }
    const entry = overrides[field];
    if (!entry || typeof entry !== 'object') {
        return { ...defaults };
    }

    return {
        pageTitle: typeof entry.pageTitle === 'string' && entry.pageTitle.trim().length > 0
            ? entry.pageTitle
            : defaults.pageTitle,
        singular: typeof entry.singular === 'string' && entry.singular.trim().length > 0
            ? entry.singular
            : defaults.singular,
        plural: typeof entry.plural === 'string' && entry.plural.trim().length > 0
            ? entry.plural
            : defaults.plural,
    };
}

/**
 * Convenience: build the four placeholder values typically passed
 * into i18n templates (`{Singular}`, `{singular}`, `{Plural}`,
 * `{plural}`) from a resolved label set. Returned as a plain
 * object so callers can spread it into `$t(key, { ...vars })`.
 */
export function labelPlaceholders (labels: ObjectLabels): {
    singular: string;
    Singular: string;
    plural: string;
    Plural: string;
} {
    return {
        singular: labels.singular,
        Singular: capitalize(labels.singular),
        plural: labels.plural,
        Plural: capitalize(labels.plural),
    };
}

type ObjectPropertyMetadataInput = {
    DisplayName?: string | null;
    field?: string | null;
};

/**
 * Object properties are the only event-property family we let admins
 * rename, so a rename can hide the underlying field identifier from
 * downstream users. This helper pairs the display name with the field
 * (English: `DisplayName (field)`) and delegates the layout to i18n so
 * other locales can format the pair differently. Callers pass the
 * metadata; they should never hand-roll the parenthesized form.
 */
export function formatObjectPropertyLabel (metadata: ObjectPropertyMetadataInput): string {
    const field = metadata?.field ?? '';
    const displayName = typeof metadata?.DisplayName === 'string' ? metadata.DisplayName : '';
    if (!displayName || displayName === field) {
        return field;
    }

    return $t('objectAnalyticsLabels.{displayName} ({field})', { displayName, field });
}

/**
 * Resolves the header-link label used by OA widgets that drill into
 * Object Analytics. Prefers the raw property label so a property with
 * a customized display name reads "View Report ID"; falls back to the
 * customized plural (`{Plural}`) so a property with no display name
 * still reads "View Reports" rather than an empty string.
 */
export function formatObjectPropertyHeaderLabel (
    property: { label?: string | null } | null | undefined,
    placeholders: { Plural: string; [key: string]: string },
): string {
    const rawLabel = (property?.label ?? '').trim();
    if (rawLabel) {
        return $t('objectAnalyticsLabels.View {objectPropertyLabel}', { objectPropertyLabel: rawLabel });
    }

    return $t('objectAnalyticsLabels.View {Plural}', placeholders);
}
